'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { localizedHref } from '@/lib/i18n/href';
import type { Locale } from '@/lib/i18n/config';
import { CONSENT_STORAGE_KEY, CONSENT_EVENT, type ConsentValue } from '@/lib/consent';

// Component-local strings — same convention as the individual tool
// components (see components/tools/*.tsx), not the shared dictionary,
// since this string set is specific to this one component.
//
// DRAFT translation (ar, es, ru) — machine-assisted, not reviewed by a
// native speaker or counsel. Same caveat as the privacy page copy this
// banner links to; get it checked before treating it as a compliant
// disclosure in those locales.
const T = {
  en: {
    message: 'We use analytics cookies to understand how visitors use this site. They are only set if you accept.',
    accept: 'Accept',
    decline: 'Decline',
    privacyLink: 'Privacy Policy',
  },
  ar: {
    message: 'نستخدم ملفات تعريف ارتباط تحليلية لفهم كيفية استخدام الزوار لهذا الموقع. لا يتم تفعيلها إلا بموافقتك.',
    accept: 'موافق',
    decline: 'رفض',
    privacyLink: 'سياسة الخصوصية',
  },
  es: {
    message: 'Usamos cookies de análisis para entender cómo los visitantes usan este sitio. Solo se activan si las aceptas.',
    accept: 'Aceptar',
    decline: 'Rechazar',
    privacyLink: 'Política de Privacidad',
  },
  ru: {
    message: 'Мы используем аналитические файлы cookie, чтобы понять, как посетители используют сайт. Они устанавливаются только с вашего согласия.',
    accept: 'Принять',
    decline: 'Отклонить',
    privacyLink: 'Политика конфиденциальности',
  },
  fr: {
    message: "Nous utilisons des cookies analytiques pour comprendre comment les visiteurs utilisent ce site. Ils ne sont activés que si vous acceptez.",
    accept: 'Accepter',
    decline: 'Refuser',
    privacyLink: 'Politique de confidentialité',
  },
} as const;

interface CookieConsentProps {
  locale?: Locale;
}

export default function CookieConsent({ locale = 'en' }: CookieConsentProps) {
  const t = T[locale] ?? T.en;
  // null = decision not yet read from storage (or none stored) — banner
  // stays hidden until this resolves on mount, so it never flashes for
  // returning visitors who already chose, and never shows during SSR.
  const [decision, setDecision] = useState<ConsentValue | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY) as ConsentValue | null;
      if (stored === 'accepted' || stored === 'declined') setDecision(stored);
    } catch {
      // localStorage unavailable (private browsing, etc.) — banner shows
      // every visit in that case, which is the safe default, not a bug.
    }
    setHydrated(true);
  }, []);

  const choose = (value: ConsentValue) => {
    setDecision(value);
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, value);
    } catch {
      // Choice still applies for this session even if it can't persist.
    }
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
  };

  if (!hydrated || decision !== null) return null;

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed bottom-0 inset-x-0 z-50 border-t border-borderDark bg-cardBg backdrop-blur-md shadow-card"
    >
      <div className="max-w-5xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
        <p className="text-xs text-textSecondary flex-1 text-center sm:text-start">
          {t.message}{' '}
          <Link href={localizedHref(locale, '/privacy')} className="underline hover:text-foreground">
            {t.privacyLink}
          </Link>
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => choose('declined')}
            className="px-4 py-2 text-xs font-semibold rounded-lg border border-borderDark text-textSecondary hover:text-foreground transition-colors"
          >
            {t.decline}
          </button>
          <button
            onClick={() => choose('accepted')}
            className="px-4 py-2 text-xs font-semibold rounded-lg bg-accent hover:bg-accentHover text-white transition-colors"
          >
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
